import { ComponentType } from '@angular/cdk/portal';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ReportDataTableListComponent } from 'app/ui/report-data-table-list/report-data-table-list.component';

/** Same sizing the dashboard chats have always used for these result dialogs. */
const RESULT_DIALOG = { width: '70%', maxHeight: '90dvh', panelClass: 'biggDialog', autoFocus: false } as const;

/**
 * Sizing of the compact result dialogs (the messenger). They replace `.biggDialog`, which forces 90-100vw and 85-90dvh
 * and was designed for the dashboard: the panel is capped at the viewport minus a gutter, never wider than a tablet, and
 * the table dialog gets a definite height (the table scrolls inside it). Styles for `msgr-result-dialog` live in
 * `messenger.scss` and in the table component's stylesheet.
 */
const COMPACT_DIALOG = {
    width: 'min(64rem, calc(100vw - 1rem))',
    maxWidth: 'calc(100vw - 1rem)',
    maxHeight: 'calc(100dvh - 1rem)',
    panelClass: 'msgr-result-dialog',
    autoFocus: false,
} as const;

/**
 * Loads the dashboard's full chart dialog (it can create trackers, favourite charts and open the full-page view).
 * Only the dashboard app provides it, so a host that never does (the standalone messenger) does not bundle the
 * dashboard feature code behind it. Messenger screens ask for the compact dialog instead (`compact: true`).
 */
export const CHAT_CHART_DIALOG_LOADER = new InjectionToken<() => Promise<ComponentType<unknown>>>('CHAT_CHART_DIALOG_LOADER');

/**
 * Opens the shared "table" and "chart" result dialogs for an agent reply, from either chat protocol.
 * Both chats fetch the payload their own way (`ai-chat/message-data`, `team-chat/messages/:id/data`)
 * in the same shape; this only presents it, so every host gets identical dialogs.
 */
@Injectable({ providedIn: 'root' })
export class ChatResultDialogService {
    private readonly _dialog = inject(MatDialog);
    private readonly _dashboardChartLoader = inject(CHAT_CHART_DIALOG_LOADER, { optional: true });

    openDataTable(options: { payload: unknown; name: string; messageId: number | string; agentName: string; compact?: boolean }): void {
        this._dialog.open(ReportDataTableListComponent, {
            ...(options.compact ? { ...COMPACT_DIALOG, height: 'min(46rem, calc(100dvh - 1rem))' } : RESULT_DIALOG),
            data: {
                dataTable: options.payload,
                from: 'chat',
                name: options.name,
                messageId: options.messageId,
                exportAgentName: options.agentName,
                ...(options.compact ? { compact: true } : {}),
            },
        });
    }

    /**
     * The chart components are heavy, so they are loaded the first time a chart is opened. `compact` selects the
     * view-only dialog (messenger); otherwise the dashboard's dialog is loaded through `CHAT_CHART_DIALOG_LOADER`.
     */
    async openChart(options: { payload: unknown; sessionId: string | null; messageId: number | string; compact?: boolean }): Promise<void> {
        let component: ComponentType<unknown>;
        if (options.compact) {
            ({ ChatChartDialogComponent: component } = await import('./chat-chart-dialog.component'));
        } else if (this._dashboardChartLoader) {
            component = await this._dashboardChartLoader();
        } else {
            throw new Error('No chart dialog is registered for this application.');
        }
        this._dialog.open(component, {
            ...(options.compact ? COMPACT_DIALOG : RESULT_DIALOG),
            data: { chartData: options.payload, sessionId: options.sessionId, messageId: options.messageId, from: 'chat' },
        });
    }
}
